/**
 * Top-center command bar — compact replay HUD controls.
 *
 * Icons are Tabler-style rounded strokes (see PlayerIcons) — the same voice
 * as the ±10s transport icons in the bottom bar, so both HUD strips share
 * one icon language.
 */

import { PacketListIcon, EyeIcon, EyeOffIcon, KeyboardIcon, SlidersIcon } from './PlayerIcons'

export type GlIconName = 'packets' | 'hud' | 'hudHidden' | 'hotkeys' | 'settings'

export type GlIcon = {
  key: string
  icon: GlIconName
  title: string
  keyLabel?: string
  active?: boolean
  expanded?: boolean
  onClick: () => void
}

type Props = {
  icons: GlIcon[]
}

const CELL = 26
const PAD = 3

const ICON_COMPONENT: Record<GlIconName, (props: { size?: number | string; className?: string }) => JSX.Element> = {
  packets: PacketListIcon,
  hud: EyeIcon,
  hudHidden: EyeOffIcon,
  hotkeys: KeyboardIcon,
  settings: SlidersIcon,
}

export default function TopMenuIconsGL ({ icons }: Props) {
  return (
    <div className="replayTopBar">
      <style>
        {`
          .replayTopBar {
            display: inline-flex;
            align-items: stretch;
            padding: ${PAD}px;
            gap: 1px;
            background:
              repeating-linear-gradient(
                180deg,
                transparent 0 2px,
                rgba(186, 226, 236, 0.016) 2px 3px
              ),
              linear-gradient(180deg, rgba(7, 12, 22, 0.54), rgba(4, 8, 15, 0.64));
            border: 0;
            border-radius: 0;
            backdrop-filter: blur(12px) saturate(1.32);
            -webkit-backdrop-filter: blur(12px) saturate(1.32);
            box-shadow:
              inset 0 0 10px rgba(1, 7, 12, 0.42),
              inset 0 1px rgba(215, 243, 248, 0.08),
              0 2px 9px rgba(0, 0, 0, 0.42);
          }
          .replayTopBtn {
            position: relative;
            width: ${CELL}px;
            height: ${CELL}px;
            margin: 0;
            padding: 0;
            display: grid;
            place-items: center;
            background: transparent;
            border: 0;
            border-radius: 0;
            cursor: pointer;
            outline: none;
            color: var(--text-dim, rgba(255, 255, 255, 0.5));
            transition: color 0.13s ease, background 0.13s ease, box-shadow 0.13s ease;
          }
          .replayTopBtn + .replayTopBtn {
            border-left: 1px solid rgba(139, 170, 180, 0.12);
          }
          .replayTopBtn:hover,
          .replayTopBtn:focus-visible {
            color: var(--text, rgba(255, 255, 255, 0.9));
            background: radial-gradient(
              circle at 50% 50%,
              transparent 30%,
              rgba(120, 150, 200, 0.05) 55%,
              rgba(120, 150, 200, 0.09) 78%,
              rgba(120, 150, 200, 0.13) 100%
            );
          }
          .replayTopBtn.isActive {
            color: var(--gold, #e8b84b);
            background: rgba(232, 184, 75, 0.105);
          }
          .replayTopIcon {
            display: block;
            transform: translateY(-0.5px);
            filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.72));
          }
          /* key cap pinned to the bottom-right corner of the cell */
          .replayTopKey {
            position: absolute;
            right: 0;
            bottom: -1px;
            min-width: 7px;
            height: 7px;
            padding: 0 1.2px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: rgba(216, 228, 236, 0.72);
            background: rgba(6, 12, 20, 0.86);
            border: 1px solid rgba(139, 170, 180, 0.34);
            font: 700 5px/1 var(--mono, "JetBrains Mono", monospace);
            letter-spacing: 0;
            pointer-events: none;
          }
          .replayTopBtn:hover .replayTopKey,
          .replayTopBtn:focus-visible .replayTopKey {
            color: rgba(238, 246, 250, 0.92);
            border-color: rgba(150, 178, 220, 0.46);
          }
          .replayTopBtn.isActive .replayTopKey {
            color: rgba(244, 224, 160, 0.92);
            border-color: rgba(232, 184, 75, 0.5);
          }
          /* Firefox: real-time backdrop blur is a heavy compositor path here
             (and renders as a no-op in Chromium). Drop ONLY the blur — keep the
             bar's semi-transparent fill so it stays translucent glass. */
          @supports (-moz-appearance: none) {
            .replayTopBar {
              backdrop-filter: none;
              -webkit-backdrop-filter: none;
            }
          }
        `}
      </style>

      {icons.map((ic) => {
        const active = ic.active || ic.expanded
        const Icon = ICON_COMPONENT[ic.icon]
        return (
          <button
            key={ic.key}
            type="button"
            className={active ? 'replayTopBtn isActive' : 'replayTopBtn'}
            aria-label={ic.title}
            title={ic.title}
            aria-pressed={ic.active}
            aria-expanded={ic.expanded}
            onClick={ic.onClick}
            onMouseDown={(e) => e.preventDefault()}
          >
            <Icon size={15} className="replayTopIcon" />
            {ic.keyLabel && <span className="replayTopKey" aria-hidden="true">{ic.keyLabel}</span>}
          </button>
        )
      })}
    </div>
  )
}
